import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { z } from "zod";

export const allApplicationsKey = "getAllApplications" as const;

export interface Application {
    id: string;
    name: string;
    description?: string;
    secretKey: string;
    createdAt: string;
    updatedAt: string;
    webhooks?: WebhookConfig[];
}

export interface WebhookConfig {
    id: number;
    endpoint: string;
    eventName: string[];
    active: boolean;
}

export interface ApplicationsResponse {
    data: Application[];
    total: number;
    "hydra:member"?: Application[];
    "hydra:totalItems"?: number;
}

export interface ApplicationParams {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
}

export interface ApiValidationError {
    violations: {
        propertyPath: string;
        message: string;
        code: string;
    }[];
    type: string;
    title: string;
    detail?: string;
}

export interface ApiError extends Error {
    response?: {
        data: ApiValidationError;
        status: number;
        statusText: string;
    };
    status?: number;
}

const jsonLdHeaders = {
    "Content-Type": "application/ld+json",
    Accept: "application/ld+json",
};

type Options = {
    cacheTime?: number;
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
};

export const useGetAllApplications = (options?: Options) => {
    const { cacheTime = 0, page = 1, limit = 20, search = "", sortBy, sortOrder } = options || {};

    const query = useQuery({
        queryKey: [allApplicationsKey, page, limit, search, sortBy, sortOrder],
        staleTime: 0,
        cacheTime: cacheTime,
        queryFn: async () => {
            const url = "/api/integrations/applications";

            return loggedInApiCall.get(url, {
                params: {
                    page,
                    limit,
                    search: search !== "" ? search : undefined,
                    sortBy,
                    sortOrder,
                },
                headers: jsonLdHeaders,
            });
        },
        select: (response) => {
            const applications = response.data["hydra:member"] || [];
            const total = response.data["hydra:totalItems"] || 0;

            return {
                items: applications,
                totalItems: total,
            };
        },
    });

    return {
        applications: query.data?.items,
        totalItems: query.data?.totalItems,
        ...query,
    };
};

export const ApplicationZodSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    secretKey: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    webhooks: z
        .union([
            z.array(z.string()),
            z.array(
                z.object({
                    id: z.union([z.string(), z.number()]).transform((val) => String(val)),
                    endpoint: z.string().optional(),
                    eventName: z.string().optional(),
                    active: z.boolean(),
                    url: z.string().optional(),
                    name: z.string().optional(),
                    method: z.string().optional(),
                    headers: z
                        .union([z.record(z.string()), z.array(z.any())])
                        .transform((val) => {
                            if (Array.isArray(val)) {
                                return {};
                            }
                            return val;
                        })
                        .optional(),
                    retryCount: z.number().optional(),
                }),
            ),
        ])
        .optional(),
});
